'use client'

import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import * as React from 'react'
import { clsx } from 'clsx'
import { Container } from '~/sotano-kit/primitives/container'
import { useMedia } from '~/hooks/use-media'
import { DotScreenShader } from '~/app/(mkt)/home/sections/hero/dots-bg'

import { Heading } from '~/sotano-kit/primitives/heading'
import { useGsapContext } from '~/hooks/use-gsap-context'
import { gsap } from '~/lib/gsap'
import { getOffsetTop } from '~/lib/dom-utils'

import s from './inheritance.module.scss'

const BORDER_SOLID = '252525'

export const Inheritance = ({
  sections,
  containerSize = 'small',
}: {
  sections: {
    title: string | undefined
    node: React.ReactElement
    padding?: {
      top?: boolean
      bottom?: boolean
      right?: boolean
      left?: boolean
    }
  }[]
  containerSize?: React.ComponentProps<typeof Container>['size']
}) => {
  const sectionsRefs = React.useRef<HTMLDivElement[]>([])
  const containerRef = React.useRef<HTMLDivElement>(null)
  const dashRef = React.useRef<HTMLDivElement>(null)
  const [lastInViewSectionIndex, setLastInViewSectionIndex] =
    React.useState<number>(0)
  const isDesktop = useMedia('(min-width: 1280px)')

  const lineSvgRef = React.useRef<SVGSVGElement>(null)
  const curveSvgRef = React.useRef<SVGSVGElement>(null)

  const [inheritanceCurveHeight, setInheritanceCurveHeight] = React.useState<
    string | undefined
  >()
  const [lastSectionTitleHeight, setLastSectionTitleHeight] =
    React.useState('0px')

  useGsapContext(() => {
    if (!isDesktop) return

    const dash = dashRef.current
    if (!dash) return

    if (!lineSvgRef.current || !curveSvgRef.current || !containerRef.current) {
      return
    }

    const linePath = lineSvgRef.current?.querySelector('path')
    const curvePath = curveSvgRef.current?.querySelector('path')

    if (!linePath || !curvePath) return

    const scrollTl = gsap.timeline({
      defaults: {
        ease: 'linear',
      },
      scrollTrigger: {
        scrub: 1.5,
        trigger: containerRef.current,
        start: 'top-=120px top',
        end: 'bottom-=400px bottom',
        invalidateOnRefresh: true,
      },
    })
    const lineLength = linePath?.getTotalLength() ?? 0
    const curveLength = curvePath?.getTotalLength() ?? 0

    // Normalize durations (e.g., base duration of 1s per 100 units of length)
    const totalLength = lineLength + curveLength
    const lineDuration = lineLength / totalLength
    const curveDuration = curveLength / totalLength

    scrollTl.fromTo(
      linePath,
      { drawSVG: '0% 0%' },
      { drawSVG: '100% 0%', duration: lineDuration }
    )

    scrollTl.to(
      dash,
      {
        motionPath: {
          path: linePath,
          align: linePath,
          alignOrigin: [0.5, 0.5],
          autoRotate: false,
        },
        duration: lineDuration,
        onUpdate: () => {
          if (!containerRef.current) return

          const offset = 100
          const eachSectionTitleTop = Array.from(
            containerRef.current.querySelectorAll<HTMLElement>(
              '[data-section="true"]'
            )
          ).map((section, index) => {
            const sectionTitle = section.querySelector<HTMLElement>(
              '[data-section-title]'
            )
            if (!sectionTitle) {
              if (index === 0) return 0
              return undefined
            }
            const sectionTitleTop = sectionTitle.getBoundingClientRect().top
            if (!sectionTitleTop) return undefined
            return sectionTitleTop - offset
          })
          const sectionTitleCollidingWithDash = eachSectionTitleTop.findLast(
            (top) => top !== undefined && top < dash.getBoundingClientRect().top
          )

          const collidingSectionIndex = eachSectionTitleTop.indexOf(
            sectionTitleCollidingWithDash
          )
          const lastSectionIsColliding =
            collidingSectionIndex === eachSectionTitleTop.length - 1

          if (collidingSectionIndex !== -1 && !lastSectionIsColliding) {
            setLastInViewSectionIndex(collidingSectionIndex)
          }
        },
      },
      '<0%'
    )

    scrollTl.to(
      dash,
      {
        motionPath: {
          path: curvePath,
          align: curvePath,
          alignOrigin: [0.5, 0.5],
          autoRotate: 90,
        },
        duration: curveDuration,
      },
      '>0%'
    )

    scrollTl.fromTo(
      curvePath,
      {
        drawSVG: '0% 0%',
      },
      {
        drawSVG: '100% 0%',
        duration: curveDuration,
        onStart: () => {
          if (curveSvgRef.current) {
            curveSvgRef.current.style.opacity = '1'
          }
        },
        onUpdate: () => {
          const hasReachEnd = scrollTl.scrollTrigger?.progress === 1
          if (hasReachEnd) {
            setTimeout(() => {
              setLastInViewSectionIndex(sectionsRefs.current.length - 1)
            }, 600)
          }
        },
      },
      '<0%'
    )

    setTimeout(() => {
      if (!lineSvgRef.current || !dash) return
      lineSvgRef.current.style.opacity = '1'
      lineSvgRef.current.style.visibility = 'visible'
      dash.style.opacity = '1'
      dash.style.visibility = 'visible'
    }, 1000)
  }, [isDesktop])

  React.useEffect(() => {
    const onResize = () => {
      ScrollTrigger.refresh()
    }

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  React.useLayoutEffect(() => {
    if (!sectionsRefs.current.length) return

    const lastSection = sectionsRefs.current[sectionsRefs.current.length - 1]
    if (!lastSection) return

    const lastSectionOffsetTop = getOffsetTop(lastSection, {
      finshWhenReaching: document.body,
    })
    const headerHeight =
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-height'
      ) || '0'
    if (!headerHeight) return
    const lastSectionTitle = lastSection.querySelector(
      '[data-section-title]'
    ) as HTMLElement | null
    if (lastSectionTitle) {
      setLastSectionTitleHeight(
        `${lastSectionTitle.getBoundingClientRect().height}px`
      )
    }

    setInheritanceCurveHeight(
      `calc(${lastSectionOffsetTop}px - ${headerHeight})`
    )
  }, [])

  React.useEffect(() => {
    if (!lineSvgRef.current || inheritanceCurveHeight === '0px') return

    const linePath = lineSvgRef.current?.querySelector('path')
    if (!linePath) return

    const svg = lineSvgRef.current
    const height = svg.getBoundingClientRect().height

    if (!height) return

    svg.setAttribute('viewBox', `0 0 1 ${height}`)
    linePath.setAttribute('d', `M0.5 0 L0.5 ${height}`)
  }, [inheritanceCurveHeight])

  return (
    <>
      <div
        className={clsx(
          'absolute top-0 h-[400px] xl:h-[1000px] left-0 w-screen',
          'after:content-[""] after:absolute after:w-screen after:bottom-0 after:bg-gradient-to-t after:from-dark-bg after:to-transparent after:h-60 after:pointer-events-none z-0'
        )}
      >
        <DotScreenShader />
      </div>

      <Container className="!p-0" size={containerSize} ref={containerRef}>
        {/* dash */}
        <div
          ref={dashRef}
          style={{ visibility: isDesktop ? 'visible' : 'hidden' }}
          aria-label="progress indicator"
          className={clsx(
            s['dash'],
            'absolute translate-x-[37px] md:translate-x-[40.5px] lg:translate-x-3 xl:-translate-x-[75px] w-px bg-dark-control-base transition-opacity duration-300 ease-in z-30 rounded-sm opacity-0 delay-75'
          )}
        />

        {/* progress line */}
        <svg
          width="1"
          preserveAspectRatio="xMidYMin meet"
          viewBox="0 0 1 2441"
          style={{
            height: inheritanceCurveHeight,
            visibility: isDesktop ? 'visible' : 'hidden',
            shapeRendering: 'geometricPrecision',
          }}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute translate-x-[37px] md:translate-x-[40.5px] lg:translate-x-3 xl:-translate-x-[71.5px] z-20 opacity-0 transition-opacity duration-300 ease-in"
          ref={lineSvgRef}
        >
          <path
            d="M1 0L0.999789 2395.5L0.99966 2441"
            stroke="#FF6C02"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* last curve, rail for the dash */}
        <svg
          width="76"
          height="120"
          viewBox="0 0 76 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            visibility: isDesktop ? 'visible' : 'hidden',
            top: `calc(${inheritanceCurveHeight} + ${lastSectionTitleHeight} + (${lastSectionTitleHeight} / 2))`,
          }}
          ref={curveSvgRef}
          className="absolute translate-x-[37px] md:translate-x-[40.5px] lg:translate-x-3 xl:-translate-x-18 z-20 opacity-0"
        >
          <path
            d="M1 0C1 24.4078 1 38.0922 1 62.5C1 90.1142 23.3858 112.5 51 112.5H77"
            stroke="#FF6C02"
          />
        </svg>

        <div className="relative">
          {sections.map(({ title, node, padding }, index) => {
            return (
              <Section
                ref={(ref) => {
                  if (ref) {
                    sectionsRefs.current[index] = ref
                  }
                }}
                key={index}
                title={title}
                index={index}
                isLast={index === sections.length - 1}
                padding={padding}
                highlight={lastInViewSectionIndex === index}
              >
                {node}
              </Section>
            )
          })}
        </div>
      </Container>
    </>
  )
}

const Section = ({
  ref,
  title,
  index,
  children,
  isLast,
  highlight,
  padding,
}: {
  ref: React.Ref<HTMLDivElement>
  title: string | undefined
  index: number
  children: React.ReactElement
  isLast: boolean
  highlight?: boolean
  padding?: React.ComponentProps<
    typeof Inheritance
  >['sections'][number]['padding']
}) => {
  const isMobileOrSmTablet = useMedia('(max-width: 1279px)')
  const currentBranchIllustration = !isMobileOrSmTablet
    ? highlight && !isLast
      ? BRANCH_ILLUS['desktop'].highlighted
      : BRANCH_ILLUS['desktop'].default
    : highlight
      ? BRANCH_ILLUS['mobile'].highlighted
      : BRANCH_ILLUS['mobile'].highlighted

  return (
    <Container
      ref={ref}
      data-section
      size="large"
      key={index}
      className={clsx(
        'flex flex-col pt-10 text-sm lg:text-base lg:py-24 xl:py-20 relative overflow-x-clip md:overflow-x-visible first:pt-12 sm:first:pt-20 lg:first:pt-[70px]',
        !isLast
          ? 'xl:before:content-[""] xl:before:absolute xl:before:h-[calc(100%+theme(spacing.10))] lg:before:h-full xl:before:w-px xl:before:bg-dark-border-solid xl:before:top-0 xl:before:-left-[71.5px] pb-32'
          : 'pb-18',
        {
          'md:!pt-0': padding?.top === false,
          'md:!pb-0': padding?.bottom === false,
          'md:!pr-0': padding?.right === false,
          'md:!pl-0': padding?.left === false,
        }
      )}
    >
      {!!title && (
        <>
          <div
            className={clsx(
              'absolute w-px xl:w-[76px] aspect-[1/160] xl:aspect-[76/120] bg-cover h-36 lg:h-auto -top-28 lg:-top-18 left-10 sm:left-12 xl:-left-18 xl:-top-6 transition-[background-image] duration-300 ease-out',
              { 'xl:-top-[102px]': padding?.top === false }
            )}
            data-inheritance-curve
            style={{
              backgroundRepeat: 'no-repeat',
              backgroundImage: `url("${currentBranchIllustration}")`,
            }}
          />
          <p
            className={clsx(
              'border px-3 md:px-4 py-1 max-w-max rounded-full bg-dark-base transition-colors duration-300 ease-out font-medium z-30',
              highlight || isMobileOrSmTablet
                ? 'border-dark-control-base bg-gradient-to-t from-[rgba(255,155,81,0.05)] to-[rgba(255,108,2,0.05)]'
                : 'border-dark-border-solid'
            )}
          >
            <span
              style={
                highlight || isMobileOrSmTablet
                  ? {
                      backgroundClip: 'text',
                      ['WebkitBackgroundClip' as string]: 'text',
                      ['WebkitTextFillColor' as string]: 'transparent',
                      backgroundImage:
                        'linear-gradient(180deg, #FF9B51 0%, #FF6C02 100%)',
                    }
                  : {}
              }
              data-section-title
              className={clsx(
                'font-medium',
                highlight || isMobileOrSmTablet
                  ? 'border-dark-control-base bg-gradient-to-t from-transparent via-dark-control-base/10 to-dark-base'
                  : 'text-dark-muted'
              )}
            >
              {title}
            </span>
          </p>
        </>
      )}
      <div className="z-30">{children}</div>
    </Container>
  )
}

const BRANCH_ILLUS = {
  desktop: {
    highlighted: `data:image/svg+xml,%3Csvg width='77' height='120' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 0v69.5c0 27.614 22.386 50 50 50h26' stroke='url(%23paint0_linear_8624_27688)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_8624_27688' x1='43' y1='0' x2='43' y2='119.5' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='.045' stop-color='%23FF9B51' stop-opacity='0'/%3E%3Cstop offset='.25' stop-color='%23FF9140'/%3E%3Cstop offset='1' stop-color='%23FF6C02'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E`,
    default: `data:image/svg+xml,%3Csvg width='77' height='120' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 0v69.5c0 27.614 22.386 50 50 50h26' stroke='url(%23paint0_linear_8624_27688)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_8624_27688' x1='43' y1='0' x2='43' y2='119.5' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='.045' stop-color='%23${BORDER_SOLID}' stop-opacity='0'/%3E%3Cstop offset='.25' stop-color='%23${BORDER_SOLID}'/%3E%3Cstop offset='1' stop-color='%23${BORDER_SOLID}'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E`,
  },
  mobile: {
    default: `data:image/svg+xml,%3Csvg width='2' height='160' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 .04v80' stroke='url(%23a)'/%3E%3Cpath d='M1 80v80' stroke='%23252525'/%3E%3Cdefs%3E%3ClinearGradient id='a' x1='1.5' y1='.04' x2='1.5' y2='80.04' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23${BORDER_SOLID}' stop-opacity='0'/%3E%3Cstop offset='1' stop-color='%23${BORDER_SOLID}'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E`,
    highlighted: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='2' height='160' fill='none'%3E%3Cpath stroke='url(%23a)' d='M1 0v160'/%3E%3Cdefs%3E%3ClinearGradient id='a' x1='1.5' x2='1.5' y1='0' y2='160' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23FF9B51' stop-opacity='0'/%3E%3Cstop offset='.51' stop-color='%23FF9B51'/%3E%3Cstop offset='1' stop-color='%23FF6C02'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E`,
  },
} as const

export const InheritanceHeader = ({
  title,
  subtitle,
}: {
  title: string
  subtitle: string
}) => {
  return (
    <>
      <Heading
        as="h1"
        size="large"
        className="leading-[1.1] mt-4 mb-3 text-pretty"
      >
        {title}
      </Heading>
      <h2 className="text-dark-muted leading-[1.4] text-sm sm:text-base text-pretty max-w-[800px]">
        {subtitle}
      </h2>
    </>
  )
}
